"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { ArrowIcon } from "@/components/ui/Icons";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { ContentCardFields } from "@/types/acf";

type Align = "left" | "center" | "right";

export default function ContentCard({ data }: { data: ContentCardFields }) {
  if (!data || data.hide) return null;

  const title = data.title ?? "";
  const text = data.text ?? "";

  const rawHref =
    data.button && typeof data.button === "object" ? (data.button.url ?? "") : "";
  const isAnchorLink = rawHref.trim().startsWith("#");

  const align: Align = (data.text_align as Align) ?? "left";

  const alignWrapClass =
    align === "center"
      ? "text-center items-center"
      : align === "right"
        ? "text-right items-end"
        : "text-left items-start";

  const justifyClass =
    align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start";

  const imageObj = data.image && typeof data.image === "object" ? data.image : null;
  const imageSrc = imageObj?.url ? toAbsoluteUrl(imageObj.url) : null;

  if (!title && !text && !data.button && !imageSrc) return null;

  return (
    <div id={data.id} className="w-full h-full flex flex-col overflow-hidden">
      {imageSrc ? (
        <div className="relative w-full aspect-[3/2]">
          <Image
            src={imageSrc}
            alt={imageObj?.alt || ""}
            fill
            className="object-cover"
            sizes="100vw"
          />
        </div>
      ) : null}

      {/* content fills remaining height */}
      <div className={`p-6 flex flex-col flex-1 bg-fuji-grey ${alignWrapClass}`}>
        {title ? (
          <h3 className="text-title-2 !m-0">
            <SafeHtml content={title} />
          </h3>
        ) : null}

        {text ? (
          <div className="mt-4 min-w-0 break-words [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto">
            <SafeHtml content={text} flow />
          </div>
        ) : null}

        {/* optional: push button to bottom when heights match */}
        {data.button && typeof data.button === "object" && data.button.url ? (
          <div className={`mt-auto pt-6 w-full flex ${justifyClass}`}>
            <Button
              as="a"
              href={toAbsoluteUrl(data.button.url) ?? "#"}
              target={data.button.target || "_self"}
              rel={data.button.target === "_blank" ? "noopener noreferrer" : undefined}
              icon={isAnchorLink ? <ArrowIcon direction="down" /> : undefined}
            >
              {data.button.title || "Learn more"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
